import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface SidebarGroupsContextValue {
  isExpanded: (group: string) => boolean;
  toggle: (group: string) => void;
  ensureExpanded: (group: string) => void;
}

const SidebarGroupsContext = createContext<SidebarGroupsContextValue | undefined>(undefined);

const DEFAULT_EXPANDED = ["Home"];

export function SidebarGroupsProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(DEFAULT_EXPANDED));

  const isExpanded = useCallback((group: string) => expanded.has(group), [expanded]);

  const toggle = useCallback((group: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const ensureExpanded = useCallback((group: string) => {
    setExpanded((prev) => {
      if (prev.has(group)) return prev;
      const next = new Set(prev);
      next.add(group);
      return next;
    });
  }, []);

  const value = useMemo<SidebarGroupsContextValue>(
    () => ({ isExpanded, toggle, ensureExpanded }),
    [isExpanded, toggle, ensureExpanded]
  );

  return <SidebarGroupsContext.Provider value={value}>{children}</SidebarGroupsContext.Provider>;
}

export function useSidebarGroups(): SidebarGroupsContextValue {
  const ctx = useContext(SidebarGroupsContext);
  if (!ctx) {
    throw new Error("useSidebarGroups must be used within <SidebarGroupsProvider>.");
  }
  return ctx;
}
